import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { addModal, removeModal } from 'src/features/globalSlice';
import { sendNewPassword } from 'src/store/actions/authActions';
import {
  getCompanyList,
  setCompanyList,
} from 'src/store/actions/companyActions';
import {
  getServiceAccountDetails,
  removeServiceAccount,
  setServiceAccountDetails,
} from 'src/store/actions/serviceAccountActions';
import { ModalType } from 'src/store/models/Modal';
import {
  AccountStatus,
  ServiceAccountAccess,
} from 'src/store/models/ServiceAccount';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import classes from './ServiceAccountDetails.module.scss';
import ServiceAccountDetailsForm, {
  ServiceAccountDetailsFormData,
} from './ServiceAccountDetailsForm/ServiceAccountDetailsForm';

const defaultValues: ServiceAccountDetailsFormData = {
  title: '',
  eMail: '',
  phone: '',
  company: null,
  access: '',
  enabled: AccountStatus.ACTIVATED,
  notes: '',
};

export interface ServiceAccountDetailsProps {
  edit?: boolean;
}

const ServiceAccountDetails: FC<ServiceAccountDetailsProps> = ({
  edit = false,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams();

  const methods = useForm<ServiceAccountDetailsFormData>({
    mode: 'onChange',
    defaultValues,
  });

  const loading = useLoadingSelector({
    actionType: getServiceAccountDetails.toString(),
  });
  const serviceAccountDetails = useAppSelector(
    (state) => state.serviceAccount.serviceAccountDetails
  );

  useEffect(() => {
    dispatch(setServiceAccountDetails(null));
    dispatch(setCompanyList({ count: 0, data: [], ctx: null }));
    if (edit) {
      dispatch(getServiceAccountDetails({ serviceId: params?.serviceId }));
    }
    dispatch(getCompanyList({ params: { size: 5000 } }));
    return () => {
      dispatch(setServiceAccountDetails(null));
      dispatch(setCompanyList({ count: 0, data: [], ctx: null }));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (serviceAccountDetails) {
      methods?.reset({
        ...serviceAccountDetails,
        enabled: serviceAccountDetails.enabled
          ? AccountStatus.ACTIVATED
          : AccountStatus.DEACTIVATED,
        access: serviceAccountDetails.restricted
          ? ServiceAccountAccess.LIMITED
          : ServiceAccountAccess.FULL,
        company: serviceAccountDetails?.company,
        notes: serviceAccountDetails?.notes || '',
      });
    }
    // eslint-disable-next-line
  }, [serviceAccountDetails]);

  const removeAccountHandler = () => {
    const account = methods?.getValues();
    dispatch(
      addModal({
        title: t('common:are_you_sure'),
        body: t('service_account:account_will_be_removed', {
          account: `${account?.title} (${account?.company?.title})`,
        }),
        modalType: ModalType.WARNING,
        loadingAction: removeServiceAccount.type,
        primaryButtonText: t('common:yes'),
        secondaryButtonText: t('common:no'),
        primaryAction: () => {
          dispatch(removeServiceAccount(account));
        },
        secondaryAction: () => dispatch(removeModal()),
      })
    );
  };

  const changePassword = () => {
    dispatch(
      addModal({
        title: t('settings:change_password'),
        body: `${t('login:press_button_to_reset', {
          account: serviceAccountDetails?.title,
        })} ${t('login:press_button_to_reset_service')}`,
        modalType: ModalType.SUCCESS,
        loadingAction: sendNewPassword.type,
        primaryButtonText: t('login:send_new_password'),
        secondaryButtonText: t('common:cancel'),
        primaryAction: () => {
          dispatch(
            sendNewPassword({
              userId: serviceAccountDetails?.id,
              title: serviceAccountDetails?.title,
            })
          );
        },
        secondaryAction: () => dispatch(removeModal()),
      })
    );
  };

  return (
    <FormProvider {...methods}>
      <div className={classes.Container}>
        <PageHeader
          title={edit ? t('common:edit_account') : t('common:create_new')}
          showBackButton
          backConfirmationText={
            methods?.formState.isDirty && t('common:changes_not_saved')
          }
          endNode={
            <div className={classes.HeaderRight}>
              {serviceAccountDetails && (
                <p>{`${t('service_account:active_tickets')}: ${
                  serviceAccountDetails?.activeTicketCount
                }`}</p>
              )}
              <Button
                type="submit"
                form="service-account-form"
                disabled={
                  !(methods.formState?.isValid && methods.formState?.isDirty) ||
                  loading
                }
                className={classes.HeaderButton}
              >
                {edit ? t('common:save_changes') : t('common:create_account')}
              </Button>
            </div>
          }
        />
        <ServiceAccountDetailsForm edit={edit} loading={loading} />
        {edit && !loading && (
          <footer>
            <Button onClick={changePassword} variant="outlined">
              {t('settings:change_password')}
            </Button>
            <Button onClick={removeAccountHandler} variant="outlined">
              {t('service_account:delete_account')}
            </Button>
          </footer>
        )}
      </div>
    </FormProvider>
  );
};

export default ServiceAccountDetails;
